import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Slider,
  SliderSingleProps,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import * as R from 'ramda';

import AddressEntry from '@totem/components/common/addressEntry/AddressEntry';
import BuildingContext from '@totem/components/portfolio/buildings/details/container/BuildingContext';
import {
  BuildingPatch,
  BuildingRegion,
} from '@totem/components/portfolio/buildings/details/sidebar/types';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Address, AddressFormErrors } from '@totem/types/address';
import { BuildingTypes, InvestmentPosture } from '@totem/types/building';
import { isNotNull } from '@totem/utilities/common';
import {
  BUILDINGS_ENDPOINT,
  CONFIGURATION_ENDPOINT,
  V2_BUILDINGS_ENDPOINT,
} from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { timezoneOptions } from '@totem/utilities/timezones';
const FormItem = Form.Item;
const Option = Select.Option;
const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
};

const marks: SliderSingleProps['marks'] = {
  1: 'Low',
  10: 'High',
};

export interface Props {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
}

type EditBuildingErrors = AddressFormErrors & {
  timezone?: string;
};

const FORM_FIELDS_MAP: { [key: string]: string } = {
  addressOne: 'Address',
  city: 'City',
  state: 'State',
  country: 'Country',
  zipCode: 'Zip Code',
};
const EditBuildingModal = ({ open, setOpen }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data: building, requestRefresh } = useContext(BuildingContext);
  const { doRefresh } = useContext(OrganizationContext);
  const [buildingTypes, setBuildingTypes] = useState<BuildingTypes>({
    id: '62a8d9d5134444b27707dda8',
    type: 'BuildingTypes',
    data: [
      '',
      'Commercial Office',
      'Data Center',
      'Educational',
      'Life Sciences',
      'Multi-Family High Rise',
      'Multi-Family Podium',
      'Retail',
      'Warehouse',
      'Other',
    ],
  });
  const [investmentPostures, setInvestmentPostures] = useState<
    InvestmentPosture[]
  >([
    { name: 'Conservative', description: '' },
    { name: 'Aggressive', description: '' },
    { name: 'Balanced', description: '' },
  ]);
  const [errors, setErrors] = useState<EditBuildingErrors>({});
  const [isSending, setIsSending] = useState(false);
  const [name, setName] = useState<string>(
    isNotNull(building) ? building.building.name : '',
  );
  const [type, setType] = useState<string>(
    isNotNull(building) ? building.building.type : '',
  );
  const [squareFootage, setSquareFootage] = useState<number>(
    isNotNull(building) ? building.building.squareFootage : 0,
  );
  const [timezone, setTimezone] = useState<string>(
    isNotNull(building) ? building.building.timezone : '',
  );
  const [investmentPosture, setInvestmentPosture] = useState<
    string | undefined
  >(
    isNotNull(building) && isNotNull(building.building.attributes)
      ? building.building.attributes.investmentPosture
      : undefined,
  );
  const [locationRisk, setLocationRisk] = useState<number | undefined>(
    isNotNull(building) && isNotNull(building.building.attributes)
      ? building.building.attributes.locationRisk
      : undefined,
  );
  const [tenantProfile, setTenantProfile] = useState<number | undefined>(
    isNotNull(building) && isNotNull(building.building.attributes)
      ? building.building.attributes.tenantProfile
      : undefined,
  );
  const [occupancyRate, setOccupancyRate] = useState<number | undefined>(
    isNotNull(building) && isNotNull(building.building.attributes)
      ? building.building.attributes.occupancyRate
      : undefined,
  );
  const [investmentDiscountRate, setInvestmentDiscountRate] = useState<
    number | undefined
  >(
    isNotNull(building) && isNotNull(building.building.attributes)
      ? building.building.attributes.investmentDiscountRate
      : undefined,
  );
  const [holdPeriodEndDate, setHoldPeriodEndDate] = useState<
    string | undefined
  >(
    isNotNull(building) && isNotNull(building.building.attributes)
      ? building.building.attributes.holdPeriodEndDate
      : undefined,
  );
  const [address, setAddress] = useState<Address>(
    isNotNull(building)
      ? {
          addressOne: building.building.addressOne,
          addressTwo: building.building.addressTwo,
          city: building.building.city,
          country: building.building.country,
          location: {
            lat: building.building.location.lat,
            lng: building.building.location.lng,
          },
          state: building.building.state,
          zipCode: building.building.zipCode,
        }
      : {
          addressOne: '',
          addressTwo: '',
          city: '',
          country: '',
          location: { lat: 0, lng: 0 },
          state: '',
          zipCode: '',
        },
  );
  const [region, setRegion] = useState<BuildingRegion>(
    isNotNull(building) ? building.region : { id: '', name: '' },
  );
  const canEditRegion =
    permissions.organizationalAdministrator.hasPermissions(userProfile);
  useEffect(() => {
    if (isNotNull(building)) {
      setName(building.building.name);

      setType(building.building.type);

      setSquareFootage(building.building.squareFootage);

      setTimezone(building.building.timezone);

      setRegion(building.region);

      setAddress({
        addressOne: building.building.addressOne,
        addressTwo: building.building.addressTwo,
        city: building.building.city,
        country: building.building.country,
        location: {
          lat: building.building.location.lat,
          lng: building.building.location.lng,
        },
        state: building.building.state,
        zipCode: building.building.zipCode,
      });
      if (isNotNull(building.building.attributes)) {
        setInvestmentPosture(building.building.attributes.investmentPosture);

        setLocationRisk(building.building.attributes.locationRisk);

        setTenantProfile(building.building.attributes.tenantProfile);

        setOccupancyRate(building.building.attributes.occupancyRate);

        setInvestmentDiscountRate(
          building.building.attributes.investmentDiscountRate,
        );

        setHoldPeriodEndDate(building.building.attributes.holdPeriodEndDate);
      }
    }
  }, [building]);

  useEffect(() => {
    fetch(`${BUILDINGS_ENDPOINT}/types`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: BuildingTypes) => {
        setBuildingTypes(result);
      });
  }, [getApiToken]);

  useEffect(() => {
    fetch(`${CONFIGURATION_ENDPOINT}/investmentPostures`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: InvestmentPosture[]) => {
        setInvestmentPostures(result);
      });
  }, [getApiToken]);

  const sendBuildingUpdate = useCallback(
    async (payload: BuildingPatch) => {
      if (!isSending) {
        setIsSending(true);

        fetch(`${V2_BUILDINGS_ENDPOINT}/${building.building.id}`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);

          CheckResponseShowError(res);

          requestRefresh();

          setOpen(false);

          doRefresh();
        });
      }
    },
    [building, getApiToken],
  );

  const validate = () => {
    const nextErrors: EditBuildingErrors = {};

    if (R.isEmpty(name) || R.isNil(name)) {
      nextErrors.name = 'Name is required.';
    }

    if (R.isEmpty(timezone) || !timezoneOptions.includes(timezone)) {
      nextErrors.timezone = 'Timezone is required.';
    }

    const addressErrors = Object.keys(FORM_FIELDS_MAP).reduce(
      (acc: AddressFormErrors, key: string) => {
        const label = FORM_FIELDS_MAP[key];
        const value = address[key];
        if (R.isEmpty(value) || R.isNil(value)) {
          return {
            ...acc,
            [key]: `${label} is required.`,
          };
        }
        return acc;
      },
      {},
    );

    const mergedErrors = { ...nextErrors, ...addressErrors };
    setErrors(mergedErrors);
    return R.isEmpty(mergedErrors);
  };

  const onCancel = () => {
    setOpen(false);

    setName(isNotNull(building) ? building.building.name : '');

    setType(isNotNull(building) ? building.building.type : '');

    setSquareFootage(isNotNull(building) ? building.building.squareFootage : 0);

    setTimezone(isNotNull(building) ? building.building.timezone : '');

    setRegion(isNotNull(building) ? building.region : { id: '', name: '' });

    setAddress({
      addressOne: isNotNull(building) ? building.building.addressOne : '',
      addressTwo: isNotNull(building) ? building.building.addressTwo : '',
      city: isNotNull(building) ? building.building.city : '',
      country: isNotNull(building) ? building.building.country : '',
      location: {
        lat: isNotNull(building) ? building.building.location.lat : 0,
        lng: isNotNull(building) ? building.building.location.lng : 0,
      },
      state: isNotNull(building) ? building.building.state : '',
      zipCode: isNotNull(building) ? building.building.zipCode : '',
    });

    if (isNotNull(building) && isNotNull(building.building.attributes)) {
      setInvestmentPosture(building.building.attributes.investmentPosture);

      setLocationRisk(building.building.attributes.locationRisk);

      setTenantProfile(building.building.attributes.tenantProfile);

      setOccupancyRate(building.building.attributes.occupancyRate);

      setInvestmentDiscountRate(
        building.building.attributes.investmentDiscountRate,
      );

      setHoldPeriodEndDate(building.building.attributes.holdPeriodEndDate);
    } else {
      setInvestmentPosture(undefined);

      setLocationRisk(undefined);

      setTenantProfile(undefined);

      setOccupancyRate(undefined);

      setInvestmentDiscountRate(undefined);

      setHoldPeriodEndDate(undefined);
    }

    setErrors({});
  };

  const onSave = () => {
    if (!validate()) {
      return;
    }

    const payload = {
      id: isNotNull(building) ? building.building.id : '',
      name,
      type,
      squareFootage,
      address,
      region: canEditRegion
        ? region
        : isNotNull(building)
          ? building.region
          : { id: '', name: '' },
      attributes: {
        type,
        squareFootage,
        timezone,
        investmentPosture,
        locationRisk,
        tenantProfile,
        occupancyRate,
        investmentDiscountRate,
        holdPeriodEndDate,
      },
      updatedBy: userProfile.id,
      ...address,
    };

    sendBuildingUpdate(payload);
  };

  const footer = (
    <>
      <Button onClick={onCancel}>Cancel</Button>
      <Button type="primary" onClick={onSave} loading={isSending}>
        Update
      </Button>
    </>
  );

  return (
    <Modal
      title="Update Building"
      open={open}
      onCancel={onCancel}
      footer={footer}
    >
      <Form layout="vertical" style={styles.form}>
        <FormItem
          label="Building Name"
          colon={false}
          validateStatus={R.isNil(errors.name) ? 'success' : 'error'}
          help={R.isNil(errors.name) ? null : errors.name}
          style={styles.formItem}
        >
          <Input
            value={name}
            name="name"
            onChange={(event) => setName(event.target.value)}
          />
        </FormItem>
        {canEditRegion && isNotNull(building) && (
          <FormItem
            label="Region"
            colon={false}
            validateStatus="success"
            style={styles.formItem}
          >
            <OrgHierarchySelector
              showOrganization={false}
              showRegions
              showBuildings={false}
              showControlSystems={false}
              showLabels={false}
              defaultOrganizationId={building.building.organizationId}
              defaultRegionId={building.region.id}
              onChange={(_organization, newRegion) => {
                if (newRegion.id) {
                  setRegion(newRegion);
                }
              }}
            />
          </FormItem>
        )}
      </Form>
      <br />
      <AddressEntry
        value={address}
        onChange={setAddress}
        disabled={isSending}
        errors={errors}
      />
      <br />
      <Form>
        <FormItem
          label="Building Type"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <Select
            value={type}
            onChange={(newValue: string) => setType(newValue)}
          >
            {buildingTypes.data.map((bt) => (
              <Option key={bt} value={bt}>
                {bt}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem
          label="Timezone"
          colon={false}
          validateStatus={R.isNil(errors.timezone) ? 'success' : 'error'}
          help={R.isNil(errors.timezone) ? null : errors.timezone}
          style={styles.formItem}
        >
          <Select
            value={timezone}
            showSearch={{
              optionFilterProp: 'label',
              filterOption: (input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(input.toLowerCase()),
            }}
            options={timezoneOptions.map((tz) => ({
              label: tz,
              value: tz,
            }))}
            onChange={(newValue: string) => setTimezone(newValue)}
          />
        </FormItem>
        <FormItem
          label="Square Footage"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <InputNumber
            value={squareFootage}
            onChange={(newValue: number) => setSquareFootage(newValue)}
            min={0}
          />
        </FormItem>
        <FormItem
          label="Investment Posture"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <Select
            value={investmentPosture}
            onChange={(newValue: string) => setInvestmentPosture(newValue)}
          >
            {investmentPostures.map(({ name: postureName }) => (
              <Option key={postureName} value={postureName}>
                {postureName}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem
          label="Location Risk"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <Slider
            min={1}
            max={10}
            value={locationRisk ?? 5}
            marks={marks}
            onChange={(newValue: number) => setLocationRisk(newValue)}
          />
        </FormItem>
        <FormItem
          label="Tenant Profile"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <Slider
            min={1}
            max={10}
            marks={marks}
            value={tenantProfile ?? 5}
            onChange={(newValue: number) => setTenantProfile(newValue)}
          />
        </FormItem>
        <FormItem
          label="Occupancy Rate (%)"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <InputNumber
            value={occupancyRate}
            onChange={(newValue: number) => setOccupancyRate(newValue)}
            min={0}
            max={100}
          />
        </FormItem>
        <FormItem
          label="Investment Discount Rate (%)"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <InputNumber
            value={investmentDiscountRate}
            onChange={(newValue: number) => setInvestmentDiscountRate(newValue)}
            min={0}
            max={100}
          />
        </FormItem>
        <FormItem
          label="Hold Period End Date"
          colon={false}
          validateStatus="success"
          style={styles.formItem}
        >
          <DatePicker
            value={
              holdPeriodEndDate && dayjs(holdPeriodEndDate).year() > 1000
                ? dayjs(holdPeriodEndDate)
                : null
            }
            defaultPickerValue={dayjs()}
            onChange={(value: Dayjs | null) =>
              setHoldPeriodEndDate(value ? value.toISOString() : undefined)
            }
            style={{ width: '100%' }}
          />
        </FormItem>
      </Form>
    </Modal>
  );
};
export default EditBuildingModal;
